import { useMemo, useEffect, useState, useRef } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { useLanguage } from '../../../../contexts/LanguageContext';

const GenericCategoryDropdown = ({
    value,
    onChange,
    useQueryHook,
    baseQueryArgs = {},
    dataSelector = (apiResponse) => apiResponse?.data || [],
    itemLabelField,
    itemIdField = 'lookupCode',
    placeholder,
    disabled,
    resetTrigger,
    className,
    skipQuery,
    rowCount = 25,
}) => {
    const { th, getCurrentLanguageId } = useLanguage();
    const [internalSelectedOption, setInternalSelectedOption] = useState(null);
    const [searchText, setSearchText] = useState('');
    const [options, setOptions] = useState([]);
    const prevResetTriggerRef = useRef(resetTrigger);
    const componentMounted = useRef(false);
    const [hasInteracted, setHasInteracted] = useState(false);

    useEffect(() => {
        componentMounted.current = true;
        return () => { componentMounted.current = false; };
    }, []);

    useEffect(() => {
        if (skipQuery && !disabled) {
            setHasInteracted(false);
        } else if (!skipQuery && !hasInteracted && !disabled) {
            if (componentMounted.current) {
                setHasInteracted(true);
            }
        }
    }, [skipQuery, disabled, hasInteracted]);

    const queryHookArgs = useMemo(() => ({
        ...baseQueryArgs,
        RowCount: rowCount,
        SearchText: searchText,
        LanguageId: getCurrentLanguageId(),
    }), [baseQueryArgs, searchText, rowCount, getCurrentLanguageId]);

    const actualSkip = disabled || !useQueryHook || (skipQuery && !hasInteracted);

    const { data: apiResponse, isLoading, isFetching, error } = useQueryHook(
        queryHookArgs,
        { skip: actualSkip }
    );

    useEffect(() => {
        if (apiResponse) {
            const rawItems = dataSelector(apiResponse);
            const mappedOptions = Array.isArray(rawItems) ? rawItems.map(item => {
                if (!item || typeof item[itemIdField] === 'undefined' || typeof item[itemLabelField] === 'undefined') {
                    return null;
                }
                return {
                    id: item[itemIdField],
                    label: item[itemLabelField],
                    original: item
                };
            }).filter(Boolean) : [];
            if (componentMounted.current) setOptions(mappedOptions);
        } else {
            if (componentMounted.current && !isLoading && !isFetching) {
                setOptions([]);
            }
        }
    }, [apiResponse, dataSelector, itemIdField, itemLabelField, isLoading, isFetching]);

    useEffect(() => {
        if (!componentMounted.current) return;

        if (!value) {
            if (internalSelectedOption !== null) {
                setInternalSelectedOption(null);
            }
            return;
        }

        let newOptionToSet = null;
        const valueIsObject = typeof value === 'object' && value !== null && itemIdField in value;
        const valueId = valueIsObject ? String(value[itemIdField]) : String(value);
        const valueLabel = valueIsObject ? value[itemLabelField] : null;

        if (options.length > 0) {
            const foundInOptions = options.find(opt => String(opt.id) === valueId);
            if (foundInOptions) {
                newOptionToSet = foundInOptions;
            } else {
                if (valueIsObject && valueLabel !== null) {
                    newOptionToSet = { id: valueId, label: valueLabel, original: value };
                } else {
                    const labelText = (isLoading || isFetching) ? th('common.loadingId', { id: valueId }) : th('common.categoryFieldEmpty');
                    newOptionToSet = { id: valueId, label: labelText, original: { [itemIdField]: valueId, [itemLabelField]: labelText } };
                }
            }
        } else {
            if (valueIsObject && valueLabel !== null) {
                newOptionToSet = { id: valueId, label: valueLabel, original: value };
            } else {
                const labelText = (isLoading || isFetching || (skipQuery && !hasInteracted)) ? th('common.loadingId', { id: valueId }) : th('common.idLabel', { id: valueId });
                newOptionToSet = { id: valueId, label: labelText, original: { [itemIdField]: valueId, [itemLabelField]: labelText } };
            }
        }

        const currentInternalId = internalSelectedOption ? String(internalSelectedOption.id) : null;
        const newPotentialId = newOptionToSet ? String(newOptionToSet.id) : null;

        if (currentInternalId !== newPotentialId || (newOptionToSet && internalSelectedOption?.label !== newOptionToSet.label)) {
            setInternalSelectedOption(newOptionToSet);
        }

    }, [value, options, itemIdField, itemLabelField, isLoading, isFetching, internalSelectedOption, skipQuery, hasInteracted, th]);

    useEffect(() => {
        if (resetTrigger !== prevResetTriggerRef.current && componentMounted.current) {
            setSearchText('');
            if (skipQuery) {
                setHasInteracted(false);
            }
            prevResetTriggerRef.current = resetTrigger;
        }
    }, [resetTrigger, skipQuery]);

    const prevBaseQueryArgsStringRef = useRef(JSON.stringify(baseQueryArgs));
    useEffect(() => {
        const currentBaseQueryArgsString = JSON.stringify(baseQueryArgs);
        if (currentBaseQueryArgsString !== prevBaseQueryArgsStringRef.current && componentMounted.current) {
            setSearchText('');
        }
        prevBaseQueryArgsStringRef.current = currentBaseQueryArgsString;
    }, [baseQueryArgs]);

    const handleDropdownChange = (e) => {
        const selectedOptionObject = e.value;
        if (componentMounted.current) {
            setInternalSelectedOption(selectedOptionObject);
            if (!hasInteracted) setHasInteracted(true);
        }
        if (onChange) {
            onChange(selectedOptionObject ? selectedOptionObject.original : null);
        }
    };

    const handleDropdownShowOrFocus = () => {
        if (skipQuery && !hasInteracted && componentMounted.current && !disabled) {
            setHasInteracted(true);
        }
    };

    const handleFilterChangeInternal = (e) => {
        if (skipQuery && !hasInteracted && componentMounted.current && !disabled) {
            setHasInteracted(true);
        }
        if (componentMounted.current) {
            setSearchText(e.filter);
        }
    };

    let displayOptions = [...options];
    if (internalSelectedOption && typeof internalSelectedOption.id !== 'undefined') {
        const isAlreadyInDisplay = displayOptions.some(opt => opt.id === internalSelectedOption.id);
        if (!isAlreadyInDisplay) {
            let finalLabel = internalSelectedOption.label;
            if (value && typeof value === 'object' && value[itemIdField] === internalSelectedOption.id && value[itemLabelField]) {
                finalLabel = value[itemLabelField];
            }
            displayOptions.unshift({ ...internalSelectedOption, label: finalLabel });
        }
    }

    return (
        <Dropdown
            value={internalSelectedOption}
            options={displayOptions}
            onChange={handleDropdownChange}
            onFilter={handleFilterChangeInternal}
            onShow={handleDropdownShowOrFocus}
            onFocus={handleDropdownShowOrFocus}
            optionLabel="label"
            placeholder={placeholder}
            filter
            showClear
            disabled={disabled || isLoading}
            loading={isLoading || isFetching}
            className={className}
            itemTemplate={(option) => (
                <div>{option.label}</div>
            )}
        />
    );
};

export default GenericCategoryDropdown;